import { useCallback, useMemo, useState } from 'react';
import { FolderInput, X } from 'lucide-react';
import { documentService } from '../../services/documentService';
import { logger } from '../../utils/logger';
import { useLanguage } from '../../i18n';
import FolderDestinationPicker, { ROOT_KEY } from './FolderDestinationPicker';
import { makeDestinationRule, splitMovableFolders } from '../../utils/folderMove';
import { userErrorMessage, userFacingError } from '../../utils/userError';
import Modal from '../ui/Modal';

/**
 * Move folders (#255) — the File Manager's native "Select & Activate" move dialog.
 *
 * Opened from the document context menu (between Version History and Rename).
 * The PO's mandated workflow:
 *   1. clicking Move opens a list of available destination folders,
 *   2. the confirmation button underneath starts INACTIVE,
 *   3. picking a destination ACTIVATES it,
 *   4. clicking it executes — the document moves immediately and the File
 *      Manager reflects it reactively, with zero page refresh.
 *
 * Destinations come from `GET /api/folders/tree` (one request for the whole
 * tree; `/contents` would need one per node). Two rows are deliberately
 * non-selectable:
 *   - the document's CURRENT folder (moving there is a no-op), and
 *   - PROTECTED /Financials system folders (#145) — that tree is system-managed
 *     and receipts are auto-routed into it by book + direction, so a manually
 *     placed file would be silently re-routed. They are shown (with a lock)
 *     rather than hidden, matching how the File Manager already renders and
 *     excludes them from selection.
 *
 * Portaled to <body>: the File Manager's root div closes the context menu on
 * any click, and the surrounding panels are transformed ancestors — the same
 * reason FileManagerContextMenu portals (#225 pattern).
 *
 * The destination list itself lives in the shared `FolderDestinationPicker`
 * (extracted for the chat's Manage Document dialog, #330) so both surfaces offer
 * exactly the same destinations under the same testids.
 *
 * Bulk + folders (#393 / #394): the dialog moves a SET — any mix of documents
 * and folders — in ONE `POST /api/folders/move`. A destination is blocked only
 * when it is invalid for the whole set (`utils/folderMove.makeDestinationRule`):
 * one of the selected folders, inside one (a folder can't go into its own
 * subtree), or already holding every item. Protected /Financials folders in the
 * set are left out and the dialog says so.
 */
const MoveToFolderModal = ({ document: legacyDoc, documents: docsProp, folders: foldersProp, onClose, onMoved }) => {
    const { t } = useLanguage();
    const [selectedKey, setSelectedKey] = useState(null); // null = nothing picked yet
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [tree, setTree] = useState(null); // folder-tree rows, once the picker has loaded them

    const docs = useMemo(() => docsProp ?? (legacyDoc ? [legacyDoc] : []), [docsProp, legacyDoc]);
    const { movable: movableFolders, skippedProtected } = useMemo(
        () => splitMovableFolders(foldersProp || []),
        [foldersProp],
    );
    const total = docs.length + movableFolders.length;

    const reasonFor = useMemo(
        () => makeDestinationRule({ documents: docs, folders: movableFolders, treeFolders: tree }),
        [docs, movableFolders, tree],
    );

    // "report.pdf" / "Invoices" for one item, "3 items (2 documents, 1 folder)" for a set.
    const summary = useMemo(() => {
        if (total === 1) return docs[0]?.original_filename || movableFolders[0]?.name || '';
        const parts = [];
        if (docs.length) parts.push(t('fmMoveNDocuments', '{n} document{s}', { n: docs.length, s: docs.length === 1 ? '' : 's' }));
        if (movableFolders.length) parts.push(t('fmMoveNFolders', '{n} folder{s}', { n: movableFolders.length, s: movableFolders.length === 1 ? '' : 's' }));
        return t('fmMoveNItems', '{n} items ({parts})', { n: total, parts: parts.join(', ') });
    }, [total, docs, movableFolders, t]);

    const handleMove = useCallback(async () => {
        if (selectedKey === null || busy) return;
        const target = selectedKey === ROOT_KEY ? null : selectedKey;
        setBusy(true);
        setError(null);
        try {
            // ONE call for the whole set (#393) — never a request per item.
            const res = await documentService.moveItems(
                movableFolders.map((f) => f.id),
                docs.map((d) => d.id),
                target,
            );
            // The server skips (rather than fails) anything it refuses, so a 200
            // that moved nothing must not look like success.
            if (res && ((res.moved_folders || 0) + (res.moved_documents || 0)) === 0) {
                throw userFacingError(t('fmMoveNothingMoved', 'Nothing was moved.'));
            }
            // Broadcast so surfaces outside the File Manager (the chat's document
            // cards, #330) reflect the new location immediately.
            for (const d of docs) {
                window.dispatchEvent(new CustomEvent('omnis:document-moved', {
                    detail: { id: d.id, folder_id: target },
                }));
            }
            onMoved?.();          // parent reloads the list — no page refresh
            onClose();
        } catch (e) {
            logger.error('MOVE', 'Move failed', e);
            setError(userErrorMessage(e, t));
            setBusy(false);
        }
    }, [selectedKey, busy, docs, movableFolders, onMoved, onClose, t]);

    // Escape / backdrop close (mirrors RecentlyDeletedModal); portaled by <Modal>.
    return (
        <Modal
            onClose={onClose}
            zIndex="z-[80]"
            size="lg"
            layout="column"
            cardClassName="max-h-[80dvh]"
            cardTestId="move-document-dialog"
            cardProps={{ 'data-item-count': total }}
        >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-pai-hairline">
                    <div className="flex items-center gap-3 min-w-0">
                        <FolderInput className="text-pai-meta flex-shrink-0" size={20} />
                        <div className="min-w-0">
                            <h3 className="text-[19px] font-bold text-white">{t('fmMove', 'Move')}</h3>
                            <div
                                data-testid="move-document-name"
                                className="text-[12.5px] text-pai-meta truncate"
                                title={summary}
                            >
                                {summary}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                        aria-label={t('commonClose', 'Close')}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Destination list — the shared picker (#330). */}
                <div className="flex-1 overflow-hidden px-4 pt-3 pb-1 flex flex-col min-h-0">
                    {skippedProtected.length > 0 && (
                        <div data-testid="move-protected-note" className="mb-2 text-[12.5px] text-pai-meta">
                            {t('fmMoveProtectedSkipped', "{n} system financial folder(s) can't be moved and were left out.", { n: skippedProtected.length })}
                        </div>
                    )}
                    <FolderDestinationPicker
                        knowsCurrentFolder={false}
                        blockedReason={reasonFor}
                        onTreeLoaded={setTree}
                        selectedKey={selectedKey}
                        onSelect={setSelectedKey}
                        listClassName="min-h-[180px] max-h-[46dvh]"
                    />
                </div>

                {/* Footer — the "Activate" half of the workflow. The Move button
                    exists from the start but is INACTIVE until a destination is
                    picked, so the user can see the next step without guessing. */}
                <div className="px-6 py-4 border-t border-pai-hairline">
                    {error && (
                        <div data-testid="move-error" className="mb-3 text-[13px] text-pai-red">
                            {error}
                        </div>
                    )}
                    <div className="flex items-center justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="pai-btn-ghost"
                        >
                            {t('commonCancel', 'Cancel')}
                        </button>
                        <button
                            data-testid="move-confirm"
                            disabled={selectedKey === null || busy || total === 0}
                            onClick={handleMove}
                            className={`
                                ${selectedKey === null || busy
                                    ? 'pai-btn border border-pai bg-white/[0.03] text-white/30 cursor-not-allowed'
                                    : 'pai-btn-primary'
                                }
                            `}
                        >
                            {busy ? t('fmMoving', 'Moving...') : t('fmMove', 'Move')}
                        </button>
                    </div>
                </div>
            </Modal>
    );
};

export default MoveToFolderModal;
